import React from "react";
import { BeeLogo } from "./BeeLogo";

/**
 * Calm, non-alarming error boundary for BeeNest.
 * Never shows red errors, stack traces, or urgent language.
 * Only soft prose: "Rest a moment and try again."
 */
export class SoftErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Silent log — don't surface stack to the user.
    // eslint-disable-next-line no-console
    console.warn("[BeeNest soft boundary]", error?.message, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false });
    // Navigate home gently
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isEs =
      typeof window !== "undefined" &&
      (localStorage.getItem("bn_lang") || "en") === "es";

    const heading = isEs ? "Una pausa silenciosa" : "A quiet pause";
    const body = isEs
      ? "Algo se acomodó de manera inesperada. Respira un momento y vuelve a intentar."
      : "Something settled in an unexpected way. Take a breath and try again.";
    const action = isEs ? "Volver al nido" : "Return to the nest";

    return (
      <div
        role="alert"
        aria-live="polite"
        className="min-h-screen flex items-center justify-center px-6 py-10 bn-honeycomb"
        data-testid="soft-error-boundary"
      >
        <div className="max-w-md w-full text-center bn-card p-10 bn-fade">
          <div className="flex justify-center mb-6 opacity-90">
            <BeeLogo size={88} variant="portrait" />
          </div>
          <h1
            className="display text-2xl md:text-3xl font-semibold mb-3"
            style={{ color: "var(--bn-text-primary)" }}
          >
            {heading}
          </h1>
          <p
            className="mb-8"
            style={{ color: "var(--bn-text-secondary)" }}
          >
            {body}
          </p>
          <button
            type="button"
            onClick={this.handleReset}
            className="bn-pill bn-primary-bg"
            data-testid="soft-error-reset"
          >
            {action}
          </button>
        </div>
      </div>
    );
  }
}

export default SoftErrorBoundary;
